import React, { useMemo, useState } from 'react';
import { ArrowLeftIcon, FileIcon, MailIcon, MapPinIcon, PencilIcon, PhoneIcon } from 'lucide-react';
import { Employee, ProCustomer, ProInvoice, ProJob, Quote, TimeEntry } from '../../types/pro';
import { INVOICE_STATUS, JOB_STATUS, QUOTE_STATUS, formatDate, kr, quoteTotal } from '../../utils/pro';
import { invoiceTotal } from '../../utils/invoice';
import { StatusChip } from '../../components/pro/StatusChip';
import { EmptyState } from '../../components/pro/EmptyState';
import { CustomerFormModal } from '../../components/pro/CustomerFormModal';
import { contactsOf } from '../../utils/contacts';
interface ProCustomerDetailProps {
  customer: ProCustomer;
  jobs: ProJob[];
  quotes: Quote[];
  invoices: ProInvoice[];
  timeEntries: TimeEntry[];
  employees: Employee[];
  onBack: () => void;
  onOpenJob: (jobId: string) => void;
  onOpenQuote: (quoteId: string) => void;
  onOpenInvoice: (invoiceId: string) => void;
  onUpdate: (changes: Partial<ProCustomer>) => void;
}
type Tab = 'overblik' | 'opgaver' | 'tilbud' | 'fakturaer' | 'timer' | 'dokumenter' | 'noter';
const TABS: {
  id: Tab;
  label: string;
}[] = [{
  id: 'overblik',
  label: 'Overblik'
}, {
  id: 'opgaver',
  label: 'Opgaver'
}, {
  id: 'tilbud',
  label: 'Tilbud'
}, {
  id: 'fakturaer',
  label: 'Fakturaer'
}, {
  id: 'timer',
  label: 'Timer'
}, {
  id: 'dokumenter',
  label: 'Dokumenter'
}, {
  id: 'noter',
  label: 'Noter'
}];
export function ProCustomerDetail({
  customer,
  jobs,
  quotes,
  invoices,
  timeEntries,
  employees,
  onBack,
  onOpenJob,
  onOpenQuote,
  onOpenInvoice,
  onUpdate
}: ProCustomerDetailProps) {
  const [tab, setTab] = useState<Tab>('overblik');
  const [editing, setEditing] = useState(false);
  const people = contactsOf(customer);
  const own = useMemo(() => ({
    jobs: jobs.filter(job => job.customerId === customer.id),
    quotes: quotes.filter(quote => quote.customerId === customer.id),
    invoices: invoices.filter(invoice => invoice.customerId === customer.id)
  }), [jobs, quotes, invoices, customer.id]);
  const jobIds = own.jobs.map(job => job.id);
  const hours = timeEntries.filter(entry => jobIds.includes(entry.jobId));
  const totalHours = hours.reduce((sum, entry) => sum + entry.hours, 0);
  const revenue = own.invoices.filter(invoice => invoice.status !== 'kladde').reduce((sum, invoice) => sum + invoiceTotal(invoice), 0);
  const timeline = useMemo(() => {
    const entries: {
      id: string;
      date: string;
      text: string;
    }[] = [];
    own.quotes.forEach(quote => entries.push({
      id: `q-${quote.id}`,
      date: quote.createdDate,
      text: `Tilbud ${quote.quoteNo} — ${QUOTE_STATUS[quote.status].label} · ${kr(quoteTotal(quote))}`
    }));
    own.jobs.forEach(job => entries.push({
      id: `j-${job.id}`,
      date: job.start ?? '',
      text: `Opgave ${job.jobNo} — ${job.title} · ${JOB_STATUS[job.status].label}`
    }));
    own.invoices.forEach(invoice => entries.push({
      id: `i-${invoice.id}`,
      date: invoice.invoiceDate,
      text: `Faktura ${invoice.invoiceNo} — ${INVOICE_STATUS[invoice.status].label} · ${kr(invoiceTotal(invoice))}`
    }));
    return entries.filter(entry => entry.date).sort((a, b) => b.date.localeCompare(a.date));
  }, [own]);
  const card = 'rounded-xl border border-stone-200 bg-white';
  return <div data-id="element-1025" data-mp="2he5mwowhkd15">
      <button onClick={onBack} className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-stone-500 transition-colors hover:text-stone-900" data-id="element-1026" data-mp="2hi36fbmz76lk">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-1027" data-mp="2qg55hln5fp67" />
        Alle kunder
      </button>

      <header className="mb-5 flex flex-wrap items-start justify-between gap-4" data-id="element-1028" data-mp="r4m5vxnqjt97">
        <div className="min-w-0" data-id="element-1029" data-mp="w249d8izhg9t">
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-700" data-id="element-1030" data-mp="w35t01e2fb35">
            {customer.customerNo}
            <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-stone-500" data-id="element-1031" data-mp="3rz0oabjsakpj">
              {customer.type === 'firma' ? 'Firma' : 'Privat'}
            </span>
          </p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-stone-900 sm:text-3xl" data-id="element-1032" data-mp="5slm608m6l6w">
            {customer.name}
          </h1>
          <p className="mt-1 text-sm text-stone-500" data-id="element-1033" data-mp="3e7nale8k4h2f">
            {[customer.address, `${customer.postalCode} ${customer.city}`.trim()].filter(part => part && part.trim()).join(', ')}
            {customer.cvr && ` · CVR ${customer.cvr}`}
          </p>
        </div>
        <button onClick={() => setEditing(true)} className="inline-flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3.5 py-2 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-50" data-id="element-1034" data-mp="3j55fmtjgec55">
          <PencilIcon className="h-4 w-4" data-id="element-1035" data-mp="15zgyr0mfrmia" />
          Rediger oplysninger
        </button>
      </header>

      <CustomerFormModal isOpen={editing} onClose={() => setEditing(false)} customer={customer} nextCustomerNo={customer.customerNo} onSave={changes => onUpdate(changes)} data-id="element-1036" data-mp="1aqqkvr8s07pf" />

      <div role="tablist" aria-label="Kundeoplysninger" className="mb-5 flex gap-1 overflow-x-auto rounded-xl border border-stone-200 bg-white p-1" data-id="element-1037" data-mp="111736owsujw5">
        {TABS.map(item => <button key={item.id} role="tab" aria-selected={tab === item.id} onClick={() => setTab(item.id)} className={`whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-medium transition-colors ${tab === item.id ? 'bg-brand-50 text-brand-700' : 'text-stone-500 hover:text-stone-900'}`} data-id="element-1038" data-mp="1tojoywv4ynoe">
            {item.label}
          </button>)}
      </div>

      {tab === 'overblik' && <div className="grid grid-cols-1 gap-5 lg:grid-cols-3" data-id="element-1039" data-mp="2b0hwkja6m3qu">
          <div className="space-y-5 lg:col-span-2" data-id="element-1040" data-mp="2602mq1p3zedm">
            <section className={`${card} grid grid-cols-2 gap-px overflow-hidden bg-stone-200 lg:grid-cols-4`} data-id="element-1041" data-mp="218kldcj6bbs4">
              {[{
            label: 'Opgaver',
            value: own.jobs.length
          }, {
            label: 'Tilbud',
            value: own.quotes.length
          }, {
            label: 'Registrerede timer',
            value: `${totalHours.toLocaleString('da-DK')} t`
          }, {
            label: 'Omsætning',
            value: kr(revenue)
          }].map(stat => <div key={stat.label} className="bg-white p-4" data-id="element-1042" data-mp="1dbyd553f2h5n">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400" data-id="element-1043" data-mp="3cnr34hrsem8v">
                    {stat.label}
                  </p>
                  <p className="mt-1 text-lg font-bold tabular-nums text-stone-900" data-id="element-1044" data-mp="37pfsi4to47pl">
                    {stat.value}
                  </p>
                </div>)}
            </section>

            <section className={`${card} overflow-hidden`} data-id="element-1045" data-mp="3k5z125fd7wfo">
              <header className="border-b border-stone-100 px-5 py-3.5" data-id="element-1046" data-mp="3oxqklw03t1wb">
                <h2 className="text-base font-semibold text-stone-900" data-id="element-1047" data-mp="3txbrcslimejl">
                  Historik
                </h2>
              </header>
              {timeline.length === 0 ? <EmptyState title="Ingen historik endnu" data-id="element-1048" data-mp="2x9f97jafjb69" /> : <ol className="divide-y divide-stone-100" data-id="element-1049" data-mp="318xtqdyc9k8k">
                  {timeline.map(entry => <li key={entry.id} className="flex items-start gap-3 px-5 py-3" data-id="element-1050" data-mp="366fw1lhup17l">
                      <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-stone-300" data-id="element-1051" data-mp="liba6tntcqan" />
                      <span className="min-w-0 flex-1 text-sm text-stone-700" data-id="element-1052" data-mp="qftmmu11hnne">
                        {entry.text}
                      </span>
                      <span className="flex-shrink-0 text-xs text-stone-400" data-id="element-1053" data-mp="1bkx5tloxlvis">
                        {formatDate(entry.date)}
                      </span>
                    </li>)}
                </ol>}
            </section>
          </div>

          <section className={`${card} p-5`} data-id="element-1054" data-mp="1giyklufbr8og">
            <h2 className="mb-4 text-base font-semibold text-stone-900" data-id="element-1055" data-mp="1ghxbvwls6y9w">
              Kontakt
            </h2>
            <ul className="space-y-3 text-sm" data-id="element-1056" data-mp="1q9tjpzdo62rb">
              <li data-id="element-1057" data-mp="1q91o4ok2o511">
                <a href={`tel:${customer.phone.replace(/\s/g, '')}`} className="flex items-center gap-2.5 text-stone-700 transition-colors hover:text-brand-700" data-id="element-1058" data-mp="1qbnvan31wiiq">
                  <PhoneIcon className="h-4 w-4 text-stone-400" data-id="element-1059" data-mp="1z8evnbomyjj4" />
                  {customer.phone}
                </a>
              </li>
              <li data-id="element-1060" data-mp="4x054snswg2b">
                <a href={`mailto:${customer.email}`} className="flex items-center gap-2.5 break-all text-stone-700 transition-colors hover:text-brand-700" data-id="element-1061" data-mp="4xi9ydoj42lu">
                  <MailIcon className="h-4 w-4 flex-shrink-0 text-stone-400" data-id="element-1062" data-mp="2lhvhn40gx70y" />
                  {customer.email}
                </a>
              </li>
              <li data-id="element-1063" data-mp="2bsbx7tx3x53z">
                <a href={`https://www.google.com/maps/search/${encodeURIComponent(`${customer.address}, ${customer.postalCode} ${customer.city}`)}`} target="_blank" rel="noreferrer" className="flex items-start gap-2.5 text-stone-700 transition-colors hover:text-brand-700" data-id="element-1064" data-mp="2brsfad7gigpy">
                  <MapPinIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-stone-400" data-id="element-1065" data-mp="1sv128liicxuc" />
                  <span data-id="element-1066" data-mp="3j65ihfoipoqh">
                    {customer.address}
                    <br data-id="element-1067" data-mp="3n80jsk6g8249" />
                    {customer.postalCode} {customer.city}
                  </span>
                </a>
              </li>
              {customer.leadSource && <li data-id="element-1068" data-mp="31a87c7vnorn9">
                  <span className="block text-xs uppercase tracking-wide text-stone-400" data-id="element-1069" data-mp="362hu5f1h5blb">
                    Kom fra
                  </span>
                  <span className="text-stone-900" data-id="element-1070" data-mp="2rf6kjious1nu">{customer.leadSource}</span>
                </li>}
              <li data-id="element-1071" data-mp="2wd7yjp7f9asg">
                <span className="block text-xs uppercase tracking-wide text-stone-400" data-id="element-1072" data-mp="2wals5sw9p1bt">
                  Kunde siden
                </span>
                <span className="text-stone-900" data-id="element-1073" data-mp="3p1vs082br6h3">
                  {formatDate(customer.createdDate)}
                </span>
              </li>
            </ul>

            {/* Everyone you can reach at this customer */}
            {people.length > 0 && <div className="mt-5 border-t border-stone-100 pt-4" data-id="element-1074" data-mp="3f5am7kihdrc7">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-stone-400" data-id="element-1075" data-mp="1zl4hezifgp39">
                  {people.length === 1 ? 'Kontaktperson' : `Kontaktpersoner (${people.length})`}
                </h3>
                <ul className="space-y-3" data-id="element-1076" data-mp="1r66tj6fiaixx">
                  {people.map(contact => <li key={contact.id} className="rounded-lg border border-stone-200 p-3" data-id="element-1077" data-mp="262o1rosefhg9">
                      <div className="flex items-start gap-2" data-id="element-1078" data-mp="214mh4zfbra2o">
                        <span className="min-w-0 flex-1" data-id="element-1079" data-mp="1dfbs2f7c744d">
                          <span className="block truncate text-sm font-medium text-stone-900" data-id="element-1080" data-mp="18f79mjmvbzbi">
                            {contact.name}
                          </span>
                          {contact.role && <span className="block text-xs text-stone-500" data-id="element-1081" data-mp="1ictqvcrn42yn">
                              {contact.role}
                            </span>}
                        </span>
                        {contact.primary && people.length > 1 && <span className="flex-shrink-0 rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-700" data-id="element-1082" data-mp="1fctixfrcbg8w">
                            Primær
                          </span>}
                      </div>
                      <div className="mt-2 space-y-1.5" data-id="element-1083" data-mp="1o787k8xrg34o">
                        {contact.phone && <a href={`tel:${contact.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 text-sm text-stone-600 transition-colors hover:text-brand-700" data-id="element-1084" data-mp="1t62qrbzn3tpb">
                            <PhoneIcon className="h-3.5 w-3.5 flex-shrink-0 text-stone-400" data-id="element-1085" data-mp="15dvzuscooexa" />
                            {contact.phone}
                          </a>}
                        {contact.email && <a href={`mailto:${contact.email}`} className="flex items-center gap-2 break-all text-sm text-stone-600 transition-colors hover:text-brand-700" data-id="element-1086" data-mp="2gw6stex6jle7">
                            <MailIcon className="h-3.5 w-3.5 flex-shrink-0 text-stone-400" data-id="element-1087" data-mp="57ij4nhm3pta" />
                            {contact.email}
                          </a>}
                        {contact.notes && <p className="text-xs italic text-stone-500" data-id="element-1088" data-mp="3rjz5julvj1se">
                            {contact.notes}
                          </p>}
                      </div>
                    </li>)}
                </ul>
              </div>}
          </section>
        </div>}

      {tab === 'opgaver' && <section className={`${card} overflow-hidden`} data-id="element-1089" data-mp="38ohf9vybv4o4">
          {own.jobs.length === 0 ? <EmptyState title="Ingen opgaver på denne kunde" data-id="element-1090" data-mp="1btmec4jv8r3c" /> : <ul className="divide-y divide-stone-100" data-id="element-1091" data-mp="16vkw1lu9kvsx">
              {own.jobs.map(job => <li key={job.id} data-id="element-1092" data-mp="1evz63s677ef4">
                  <button onClick={() => job.id && onOpenJob(job.id)} className="flex w-full items-center gap-4 px-5 py-3.5 text-left transition-colors hover:bg-stone-50" data-id="element-1093" data-mp="1ex1xe17fnivk">
                    <span className="min-w-0 flex-1" data-id="element-1094" data-mp="150p434fsv71c">
                      <span className="block truncate text-sm font-medium text-stone-900" data-id="element-1095" data-mp="19w4hxwdt52xr">
                        {job.title}
                      </span>
                      <span className="block text-xs text-stone-500" data-id="element-1096" data-mp="10whfd79cdr34">
                        {job.jobNo} · {formatDate(job.start ?? '')}
                      </span>
                    </span>
                    <StatusChip meta={JOB_STATUS[job.status]} data-id="element-1097" data-mp="r6xxa3tge19b" />
                  </button>
                </li>)}
            </ul>}
        </section>}

      {tab === 'tilbud' && <section className={`${card} overflow-hidden`} data-id="element-1098" data-mp="h8ieco4kxyaw">
          {own.quotes.length === 0 ? <EmptyState title="Ingen tilbud på denne kunde" data-id="element-1099" data-mp="3upkkfrw8jtkv" /> : <ul className="divide-y divide-stone-100" data-id="element-1100" data-mp="3uroy31jxs6yc">
              {own.quotes.map(quote => <li key={quote.id} data-id="element-1101" data-mp="3pt3z15gf7tbh">
                  <button onClick={() => quote.id && onOpenQuote(quote.id)} className="flex w-full items-center gap-4 px-5 py-3.5 text-left transition-colors hover:bg-stone-50" data-id="element-1102" data-mp="7legx5kso9lg">
                    <span className="min-w-0 flex-1" data-id="element-1103" data-mp="hhhioabqi67v">
                      <span className="block truncate text-sm font-medium text-stone-900" data-id="element-1104" data-mp="ckr4jsjhpkp3">
                        {quote.title}
                      </span>
                      <span className="block text-xs text-stone-500" data-id="element-1105" data-mp="qdqfdh8buqqb">
                        {quote.quoteNo} · {formatDate(quote.createdDate)}
                      </span>
                    </span>
                    <span className="text-sm font-semibold tabular-nums text-stone-900" data-id="element-1106" data-mp="107pj2ko1uikd">
                      {kr(quoteTotal(quote))}
                    </span>
                    <StatusChip meta={QUOTE_STATUS[quote.status]} data-id="element-1107" data-mp="3p3qe0xm1qkt3" />
                  </button>
                </li>)}
            </ul>}
        </section>}

      {tab === 'fakturaer' && <section className={`${card} overflow-hidden`} data-id="element-1108" data-mp="1wfxqpj2qwre">
          {own.invoices.length === 0 ? <EmptyState title="Ingen fakturaer på denne kunde" data-id="element-1109" data-mp="6qaata46cdhi" /> : <ul className="divide-y divide-stone-100" data-id="element-1110" data-mp="bmr2ufehvbzv">
              {own.invoices.map(invoice => <li key={invoice.id} data-id="element-1111" data-mp="glkd7yyhimvt">
                  <button onClick={() => invoice.id && onOpenInvoice(invoice.id)} className="flex w-full items-center gap-4 px-5 py-3.5 text-left transition-colors hover:bg-stone-50" data-id="element-1112" data-mp="goq67pa8i8wt">
                    <span className="min-w-0 flex-1" data-id="element-1113" data-mp="2mgwvixqfrdh0">
                      <span className="block text-sm font-medium text-stone-900" data-id="element-1114" data-mp="2mi7zoynsmn8n">
                        {invoice.invoiceNo}
                      </span>
                      <span className="block text-xs text-stone-500" data-id="element-1115" data-mp="2jw7a1ag330my">
                        Forfalder {formatDate(invoice.dueDate)}
                      </span>
                    </span>
                    <span className="text-sm font-semibold tabular-nums text-stone-900" data-id="element-1116" data-mp="2a6nqe2kjr2r1">
                      {kr(invoiceTotal(invoice))}
                    </span>
                    <StatusChip meta={INVOICE_STATUS[invoice.status]} data-id="element-1117" data-mp="32u9urd1lmbey" />
                  </button>
                </li>)}
            </ul>}
        </section>}

      {tab === 'timer' && <section className={`${card} overflow-hidden`} data-id="element-1118" data-mp="2svkibzsh1j63">
          {hours.length === 0 ? <EmptyState title="Ingen registrerede timer" data-id="element-1119" data-mp="2ov1pq3ir892u" /> : <>
              <ul className="divide-y divide-stone-100" data-id="element-1120" data-mp="1hgowntveqhg1">
                {hours.map(entry => {
            const job = own.jobs.find(j => j.id === entry.jobId);
            const employee = employees.find(e => e.id === entry.employeeId);
            return <li key={entry.id} className="flex items-center gap-4 px-5 py-3" data-id="element-1121" data-mp="3hk999e01xloi">
                      <span className="min-w-0 flex-1" data-id="element-1122" data-mp="33lrrry0qp5f1">
                        <span className="block truncate text-sm text-stone-900" data-id="element-1123" data-mp="38jb2iny8b028">
                          {job?.title ?? 'Opgave'}
                        </span>
                        <span className="block text-xs text-stone-500" data-id="element-1124" data-mp="441ug8tx8emm">
                          {employee?.name ?? 'Ukendt'} · {entry.workType} ·{' '}
                          {formatDate(entry.date)}
                        </span>
                      </span>
                      <span className="text-sm font-semibold tabular-nums text-stone-900" data-id="element-1125" data-mp="3rf9kibxkzhvj">
                        {entry.hours.toLocaleString('da-DK')} t
                      </span>
                    </li>;
          })}
              </ul>
              <div className="flex items-center justify-between border-t border-stone-200 bg-stone-50 px-5 py-3" data-id="element-1126" data-mp="x619zf7yy206">
                <span className="text-sm font-medium text-stone-600" data-id="element-1127" data-mp="t4ppfo3yc6yu">
                  I alt
                </span>
                <span className="text-sm font-bold tabular-nums text-stone-900" data-id="element-1128" data-mp="16w4ae0hf6i8j">
                  {totalHours.toLocaleString('da-DK')} timer
                </span>
              </div>
            </>}
        </section>}

      {tab === 'dokumenter' && <EmptyState icon={FileIcon} title="Ingen dokumenter" description="Tegninger, aftaler og rapporter samles her når de lægges på en opgave." data-id="element-1129" data-mp="a7olm6m4wcwh" />}

      {tab === 'noter' && <section className={`${card} p-5`} data-id="element-1130" data-mp="j608bxjvqqjn">
          <label className="block" data-id="element-1131" data-mp="1zlbctajzvrz2">
            <span className="mb-2 block text-sm font-medium text-stone-700" data-id="element-1132" data-mp="1zk9viocdy98w">
              Interne noter om kunden
            </span>
            <textarea value={customer.notes} onChange={e => onUpdate({
          notes: e.target.value
        })} rows={8} placeholder="Adgangsforhold, aftaler, hvad man skal huske…" className="w-full rounded-xl border border-stone-200 px-4 py-3 text-sm leading-relaxed outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500" data-id="element-1133" data-mp="1uouly8leuzia" />
          </label>
        </section>}
    </div>;
}